import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { CalendarClock, Copy, ExternalLink, Pencil, Plus, Power, PowerOff, Clock, Users, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import RecruiterBooking from "./RecruiterBooking";

export default function SchedulingPages() {
  const navigate = useNavigate();
  const [pages, setPages] = useState(null);
  const [creating, setCreating] = useState(false);
  const [standards, setStandards] = useState([]);
  const [bookingPage, setBookingPage] = useState(null);

  const load = () => api.get("/scheduling-pages").then((r) => setPages(r.data.items)).catch((e) => { toast.error(apiError(e)); setPages([]); });
  useEffect(() => { load(); api.get("/standard-meetings?adoptable=1").then((r) => setStandards(r.data.items || [])).catch(() => {}); }, []);

  const adopt = async (m) => {
    try {
      const { data } = await api.post(`/standard-meetings/${m.id}/adopt`);
      toast.success(data.already_adopted ? "Already on your page — opening editor" : `Adopted "${m.name}"`);
      navigate(`/calendar/scheduling-pages/${data.page_id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/scheduling-pages", { name: "Schedule a Conversation" });
      toast.success("Scheduling page created");
      navigate(`/calendar/scheduling-pages/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };

  const copyLink = (url) => { navigator.clipboard?.writeText(url); toast.success("Scheduling link copied"); };
  const toggleActive = async (p) => {
    try { await api.patch(`/scheduling-pages/${p.id}`, { active: !p.active }); toast.success(p.active ? "Page deactivated" : "Page activated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Scheduling Pages" description="Create and manage your public booking pages. Prospects pick a time; conflicts on your calendar are blocked automatically."
        actions={<Button onClick={create} disabled={creating} className="bg-brand hover:bg-brand-strong" data-testid="create-scheduling-page"><Plus className="w-4 h-4 mr-1.5" />Create Scheduling Page</Button>} />

      {standards.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 mb-4" data-testid="standard-meetings-available">
          <h3 className="font-semibold text-slate-800">Standard Meetings Available</h3>
          <p className="text-sm text-slate-400 mb-3">Adopt an account standard onto your Scheduling Page, then configure availability and provider.</p>
          <div className="grid gap-3 sm:grid-cols-3">
            {standards.map((m) => (
              <div key={m.id} className="rounded-lg border border-slate-200 p-3 flex flex-col" data-testid={`adopt-card-${m.id}`}>
                <p className="font-medium text-slate-800 text-sm">{m.name}</p>
                <p className="text-xs text-slate-400">{m.duration_min} min</p>
                {m.required_form && <span className="mt-1 text-[10px] text-violet-600">Requires Pre-Screening</span>}
                <Button size="sm" variant="outline" className="mt-3" onClick={() => adopt(m)} data-testid={`adopt-btn-${m.id}`}>Adopt</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {pages === null ? <p className="text-slate-400 py-16 text-center">Loading…</p>
        : pages.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="scheduling-empty">
            <CalendarClock className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">No scheduling pages yet</p>
            <p className="text-sm text-slate-400 mt-1 mb-4">Create a page so prospects can book time with you.</p>
            <Button onClick={create} disabled={creating} className="bg-brand hover:bg-brand-strong" data-testid="create-scheduling-page-empty"><Plus className="w-4 h-4 mr-1.5" />Create Scheduling Page</Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2" data-testid="scheduling-pages-list">
            {pages.map((p) => {
              const types = (p.appointment_types || []).filter((t) => t.active !== false);
              return (
                <div key={p.id} data-testid={`sp-card-${p.id}`} className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-slate-800 truncate">{p.name}</h3>
                      <p className="text-sm text-slate-500 truncate">{p.branding?.display_name || "—"}{p.branding?.title ? ` · ${p.branding.title}` : ""}</p>
                    </div>
                    <span data-testid={`sp-status-${p.id}`} className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Active" : "Inactive"}</span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {types.length ? types.map((t) => <span key={t.id} className="text-[11px] bg-brand/10 text-brand rounded-full px-2 py-0.5">{t.name} · {t.duration_min}m</span>)
                      : <span className="text-xs text-slate-400">No active appointment types</span>}
                  </div>

                  <div className="mt-3 flex items-center gap-2 text-xs text-slate-400 truncate">
                    <ExternalLink className="w-3.5 h-3.5 shrink-0" /><span className="truncate" data-testid={`sp-url-${p.id}`}>{p.public_url}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-xs text-slate-400">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{p.upcoming_bookings || 0} upcoming</span>
                    {p.next_booking && <span>Next: {new Date(p.next_booking).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>}
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => setBookingPage(p)} data-testid={`sp-book-${p.id}`} className="bg-brand hover:bg-brand-strong"><CalendarPlus className="w-3.5 h-3.5 mr-1" />Book for Candidate</Button>
                    <Button size="sm" variant="outline" onClick={() => copyLink(p.public_url)} data-testid={`sp-copy-${p.id}`}><Copy className="w-3.5 h-3.5 mr-1" />Copy Link</Button>
                    <Button size="sm" variant="outline" onClick={() => window.open(p.public_url, "_blank")} data-testid={`sp-preview-${p.id}`}><ExternalLink className="w-3.5 h-3.5 mr-1" />Preview</Button>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/calendar/scheduling-pages/${p.id}`)} data-testid={`sp-edit-${p.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/calendar/scheduling-pages/${p.id}/groups`)} data-testid={`sp-groups-${p.id}`}><Users className="w-3.5 h-3.5 mr-1" />Group Sessions</Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(p)} data-testid={`sp-toggle-${p.id}`} className={p.active ? "text-slate-600" : "text-emerald-600 border-emerald-200"}>
                      {p.active ? <><PowerOff className="w-3.5 h-3.5 mr-1" />Deactivate</> : <><Power className="w-3.5 h-3.5 mr-1" />Activate</>}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      {bookingPage && <RecruiterBooking page={bookingPage} onClose={() => setBookingPage(null)} onBooked={load} />}
    </div>
  );
}
